import { cardChannels } from '../models/channels';
import { CHANNEL_KEYS, CATEGORY_KEYS, CATEGORY_NAMES } from '../constants/keys';
import React from 'react';
import {
  ShoppingBag,
  Globe,
  Utensils,
  ArrowLeftRight,
  Smartphone,
  Flag,
  PlayCircle,
  Home,
} from 'lucide-react';

export type ChannelCategoryTheme = {
  id: string;
  name: string;
  icon: React.ReactNode;
  gradientFrom: string;
  gradientTo: string;
  textColor: string;
  badgeBg: string;
  badgeText: string;
  channelIds: string[];
  hidden?: boolean;
};

// Helper function to automatically map channels to their categories
const getChannelsByPrefix = (prefix: string): string[] => {
  // Category-specific channel mappings
  const categoryChannelMap: Record<string, string[]> = {
    [CATEGORY_KEYS.ONLINE_SHOPPING]: [
      CHANNEL_KEYS.ONLINE_SHOPPING,
      CHANNEL_KEYS.SHOPEE,
      CHANNEL_KEYS.MOMO,
      CHANNEL_KEYS.PCHOME,
      CHANNEL_KEYS.COUPANG,
    ],
    [CATEGORY_KEYS.TRAVEL]: [
      CHANNEL_KEYS.TRAVEL,
      CHANNEL_KEYS.KKDAY,
      CHANNEL_KEYS.KLOOK,
      CHANNEL_KEYS.AGODA,
      CHANNEL_KEYS.BOOKING,
      CHANNEL_KEYS.MILEAGE,
      CHANNEL_KEYS.HOTEL,
      CHANNEL_KEYS.GETYOURGUIDE,
    ],
    [CATEGORY_KEYS.DINING]: [
      CHANNEL_KEYS.DINING,
      CHANNEL_KEYS.FOOD_DELIVERY,
      CHANNEL_KEYS.RESTAURANT,
      CHANNEL_KEYS.CAFE,
    ],
    [CATEGORY_KEYS.TRANSPORTATION]: [
      CHANNEL_KEYS.TRANSPORTATION,
      CHANNEL_KEYS.GAS_STATION,
      CHANNEL_KEYS.SHARED_SCOOTER,
      CHANNEL_KEYS.TAIWAN_HIGH_SPEED_RAILWAY,
    ],
    [CATEGORY_KEYS.MOBILE_PAYMENT]: [
      CHANNEL_KEYS.MOBILE_PAYMENT,
      CHANNEL_KEYS.LINE_PAY,
      CHANNEL_KEYS.APPLE_PAY,
      CHANNEL_KEYS.JKOPAY,
      CHANNEL_KEYS.PXPAYPLUS,
      CHANNEL_KEYS.DIGITAL_BANKING,
      CHANNEL_KEYS.TAIWAN_PAY,
      CHANNEL_KEYS.TAISHIN_PAY,
    ],
    [CATEGORY_KEYS.ENTERTAINMENT]: [
      CHANNEL_KEYS.ENTERTAINMENT,
      CHANNEL_KEYS.STREAMING,
      CHANNEL_KEYS.MOVIE,
    ],
    [CATEGORY_KEYS.OVERSEAS]: [
      CHANNEL_KEYS.OVERSEAS,
      CHANNEL_KEYS.JAPAN,
      CHANNEL_KEYS.THAILAND,
      CHANNEL_KEYS.KOREA,
      CHANNEL_KEYS.SOUTHEAST_ASIA,
      CHANNEL_KEYS.EUROPE,
      CHANNEL_KEYS.AMERICA,
    ],
    [CATEGORY_KEYS.DAILY_LIFE]: [
      CHANNEL_KEYS.DAILY_LIFE,
      CHANNEL_KEYS.CONVENIENCE_STORE,
      CHANNEL_KEYS.DEPARTMENT_STORE,
      CHANNEL_KEYS.SUPERMARKET,
      CHANNEL_KEYS.WAREHOUSE_CLUB,
      CHANNEL_KEYS.BUSINESS,
      CHANNEL_KEYS.DIGITAL_CHANNEL,
      CHANNEL_KEYS.AIRPORT_LOUNGE,
      CHANNEL_KEYS.GYM,
      CHANNEL_KEYS.TAX,
      CHANNEL_KEYS.INSURANCE,
      CHANNEL_KEYS.NO_FEE,
    ],
  };

  // Return the predefined channels for this category
  return categoryChannelMap[prefix] || [];
};

export const channelCategories = [
  {
    id: CATEGORY_KEYS.ONLINE_SHOPPING,
    name: CATEGORY_NAMES[CATEGORY_KEYS.ONLINE_SHOPPING],
    icon: <ShoppingBag className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-purple-50 dark:from-purple-900/20',
    gradientTo: 'to-indigo-50 dark:to-indigo-900/20',
    textColor: 'text-indigo-600 dark:text-indigo-400',
    badgeBg: 'bg-indigo-100 dark:bg-indigo-900/50',
    badgeText: 'text-indigo-800 dark:text-indigo-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.ONLINE_SHOPPING),
    hidden: false,
  },
  {
    id: CATEGORY_KEYS.TRAVEL,
    name: CATEGORY_NAMES[CATEGORY_KEYS.TRAVEL],
    icon: <Globe className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-blue-50 dark:from-blue-900/20',
    gradientTo: 'to-cyan-50 dark:to-cyan-900/20',
    textColor: 'text-blue-600 dark:text-blue-400',
    badgeBg: 'bg-blue-100 dark:bg-blue-900/50',
    badgeText: 'text-blue-800 dark:text-blue-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.TRAVEL),
    hidden: false,
  },
  {
    id: CATEGORY_KEYS.DINING,
    name: CATEGORY_NAMES[CATEGORY_KEYS.DINING],
    icon: <Utensils className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-amber-50 dark:from-amber-900/20',
    gradientTo: 'to-orange-50 dark:to-orange-900/20',
    textColor: 'text-amber-600 dark:text-amber-400',
    badgeBg: 'bg-amber-100 dark:bg-amber-900/50',
    badgeText: 'text-amber-800 dark:text-amber-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.DINING),
    hidden: false,
  },
  {
    id: CATEGORY_KEYS.TRANSPORTATION,
    name: CATEGORY_NAMES[CATEGORY_KEYS.TRANSPORTATION],
    icon: <ArrowLeftRight className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-green-50 dark:from-green-900/20',
    gradientTo: 'to-teal-50 dark:to-teal-900/20',
    textColor: 'text-green-600 dark:text-green-400',
    badgeBg: 'bg-green-100 dark:bg-green-900/50',
    badgeText: 'text-green-800 dark:text-green-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.TRANSPORTATION),
    hidden: false,
  },
  {
    id: CATEGORY_KEYS.MOBILE_PAYMENT,
    name: CATEGORY_NAMES[CATEGORY_KEYS.MOBILE_PAYMENT],
    icon: <Smartphone className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-violet-50 dark:from-violet-900/20',
    gradientTo: 'to-purple-50 dark:to-purple-900/20',
    textColor: 'text-violet-600 dark:text-violet-400',
    badgeBg: 'bg-violet-100 dark:bg-violet-900/50',
    badgeText: 'text-violet-800 dark:text-violet-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.MOBILE_PAYMENT),
    hidden: false,
  },
  {
    id: CATEGORY_KEYS.OVERSEAS,
    name: CATEGORY_NAMES[CATEGORY_KEYS.OVERSEAS],
    icon: <Flag className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-rose-50 dark:from-rose-900/20',
    gradientTo: 'to-red-50 dark:to-red-900/20',
    textColor: 'text-rose-600 dark:text-rose-400',
    badgeBg: 'bg-rose-100 dark:bg-rose-900/50',
    badgeText: 'text-rose-800 dark:text-rose-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.OVERSEAS),
    hidden: false,
  },
  {
    id: CATEGORY_KEYS.ENTERTAINMENT,
    name: CATEGORY_NAMES[CATEGORY_KEYS.ENTERTAINMENT],
    icon: <PlayCircle className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-pink-50 dark:from-pink-900/20',
    gradientTo: 'to-purple-50 dark:to-purple-900/20',
    textColor: 'text-pink-600 dark:text-pink-400',
    badgeBg: 'bg-pink-100 dark:bg-pink-900/50',
    badgeText: 'text-pink-800 dark:text-pink-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.ENTERTAINMENT),
    hidden: false,
  },
  {
    id: CATEGORY_KEYS.DAILY_LIFE,
    name: CATEGORY_NAMES[CATEGORY_KEYS.DAILY_LIFE],
    icon: <Home className="w-6 h-6 mr-2" />,
    gradientFrom: 'from-yellow-50 dark:from-yellow-900/20',
    gradientTo: 'to-amber-50 dark:to-amber-900/20',
    textColor: 'text-yellow-600 dark:text-yellow-400',
    badgeBg: 'bg-yellow-100 dark:bg-yellow-900/50',
    badgeText: 'text-yellow-800 dark:text-yellow-300',
    channelIds: getChannelsByPrefix(CATEGORY_KEYS.DAILY_LIFE),
    hidden: false,
  },
] as ChannelCategoryTheme[];

export function getChannelsByCategory(categoryId: string) {
  const category = channelCategories.find(cat => cat.id === categoryId);
  if (!category) return [];

  return cardChannels.filter(channel => category.channelIds.includes(channel.id));
}
